import { useForm, usePage, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import { FlashMessages } from "@/components/FlashMessages"
import { FormError } from "@/components/FormError"
import {
  Plus,
  Pencil,
  Trash2,
  Tag,
} from "lucide-react"
import { useSortableData } from "@/hooks/useSortableData"
import { noAutofillProps, randomColor } from "@/lib/form"
import type { AppPageProps } from "@/types"

interface TagItem {
  id: string
  name: string
  color: string
  created_at: string
}

interface TagsPageProps extends AppPageProps {
  tags: TagItem[]
}

type TagSortKey = "name" | "created_at"

const tagComparators: Record<TagSortKey, (a: TagItem, b: TagItem) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  created_at: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
}

export default function Tags() {
  const { t } = useTranslation()
  const { auth, flash, tags } = usePage<TagsPageProps>().props
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<TagItem | null>(null)

  const {
    sortedData: sortedTags,
    sortKey: tagSortKey,
    sortDirection: tagSortDirection,
    handleSort: handleTagSort,
  } = useSortableData(tags, "name", tagComparators)

  const createForm = useForm({
    name: "",
    color: "#3b82f6",
  })

  const editForm = useForm({
    name: "",
    color: "#3b82f6",
  })

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    createForm.post("/tags", {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingTag) return

    editForm.patch(`/tags/${editingTag.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingTag(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(tag: TagItem) {
    setEditingTag(tag)
    editForm.setData({
      name: tag.name,
      color: tag.color,
    })
  }

  function handleDeleteTag(tag: TagItem) {
    if (confirm(t("tags.confirmDelete"))) {
      router.delete(`/tags/${tag.id}`)
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("tags.title")}</h2>
            <p className="text-muted-foreground">{t("tags.subtitle")}</p>
          </div>

          {/* Create Dialog */}
          <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
            setIsCreateDialogOpen(open)
            if (open) {
              createForm.setData("color", randomColor())
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t("tags.addButton")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleCreateSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("tags.addButton")}</DialogTitle>
                  <DialogDescription>
                    {t("tags.addDescription")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">{t("common.name")}</Label>
                    <Input
                      id="create-name"
                      value={createForm.data.name}
                      onChange={(e) => createForm.setData("name", e.target.value)}
                      placeholder={t("tags.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={createForm.errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-color">{t("tags.colorLabel")}</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="create-color"
                        type="color"
                        value={createForm.data.color}
                        onChange={(e) => createForm.setData("color", e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        {...noAutofillProps}
                      />
                      <Input
                        value={createForm.data.color}
                        onChange={(e) => createForm.setData("color", e.target.value)}
                        placeholder="#3b82f6"
                        className="flex-1"
                        maxLength={7}
                        {...noAutofillProps}
                      />
                    </div>
                    <FormError error={createForm.errors.color} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={createForm.processing}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("common.add")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={!!editingTag} onOpenChange={(open) => !open && setEditingTag(null)}>
            <DialogContent>
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("tags.editTag")}</DialogTitle>
                  <DialogDescription>{t("tags.editDescription")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t("common.name")}</Label>
                    <Input
                      id="edit-name"
                      value={editForm.data.name}
                      onChange={(e) => editForm.setData("name", e.target.value)}
                      placeholder={t("tags.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={editForm.errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-color">{t("tags.colorLabel")}</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="edit-color"
                        type="color"
                        value={editForm.data.color}
                        onChange={(e) => editForm.setData("color", e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        {...noAutofillProps}
                      />
                      <Input
                        value={editForm.data.color}
                        onChange={(e) => editForm.setData("color", e.target.value)}
                        placeholder="#3b82f6"
                        className="flex-1"
                        maxLength={7}
                        {...noAutofillProps}
                      />
                    </div>
                    <FormError error={editForm.errors.color} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingTag(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tags Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              {t("tags.section")}
            </CardTitle>
            <CardDescription>{t("tags.sectionDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {tags.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("tags.noTags")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">{t("tags.colorLabel")}</TableHead>
                    <SortableTableHead
                      label={t("common.name")}
                      sortKey="name"
                      currentSortKey={tagSortKey}
                      direction={tagSortDirection}
                      onSort={handleTagSort}
                    />
                    <SortableTableHead
                      label={t("common.createdAt")}
                      sortKey="created_at"
                      currentSortKey={tagSortKey}
                      direction={tagSortDirection}
                      onSort={handleTagSort}
                    />
                    <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTags.map((tag, index) => (
                    <TableRow key={tag.id} className={index % 2 === 1 ? "bg-muted" : ""}>
                      <TableCell>
                        <div
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: tag.color }}
                          title={tag.color}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{tag.name}</TableCell>
                      <TableCell>{formatDate(tag.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(tag)}
                            title={t("tags.editTag")}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteTag(tag)}
                            className="text-destructive hover:text-destructive"
                            title={t("tags.deleteTag")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
